<script setup lang="ts">
// An <audio>/<video> element that plays a signed library URL, using native HLS where
// available (Safari) and hls.js (loaded on demand) elsewhere, with our own controls (or
// none: `bare`, where the page draws its own, like the play bar). Given `reload`, a source
// that stops loading (a stream that failed once stays failed) is asked for again, and
// playback carries on where it was.
import { onBeforeUnmount, ref, watch } from 'vue'
import type { PlayResponse } from '../api/types'
import MediaControls from './MediaControls.vue'

const props = defineProps<{
  play: PlayResponse
  kind: 'audio' | 'video'
  autoplay?: boolean
  bare?: boolean
  /** Previous and next in the controls (a queue is playing). */
  skip?: { previous: boolean; next: boolean }
  /** A fresh source for the same file, when this one fails to load. */
  reload?: () => Promise<PlayResponse>
}>()
const emit = defineEmits<{ element: [el: HTMLMediaElement]; previous: []; next: [] }>()
const el = ref<HTMLMediaElement | null>(null)
const frame = ref<HTMLElement | null>(null)
const controls = ref<InstanceType<typeof MediaControls> | null>(null)
const failed = ref<string | null>(null)
let hls: { destroy(): void } | null = null
let alive = true
/** Fresh sources asked for since one last played. */
let retries = 0
const MAX_RETRIES = 3

async function attach(): Promise<void> {
  const media = el.value
  if (!media || !alive) return
  hls?.destroy()
  hls = null
  failed.value = null
  if (!props.play.hls || media.canPlayType('application/vnd.apple.mpegurl')) {
    media.src = props.play.url
    media.onerror = () => void recover(media)
    media.onplaying = () => (retries = 0)
  } else {
    const { default: Hls } = await import('hls.js')
    // Unmounted (or handed another element) while hls.js loaded: a removed element still
    // plays, unseen and unstoppable, if we go on.
    if (!alive || el.value !== media) return
    if (!Hls.isSupported()) {
      failed.value = 'This browser can’t play this video.'
      return
    }
    const h = new Hls()
    h.on(Hls.Events.ERROR, (_e, data) => {
      if (data.fatal) failed.value = `Playback failed (${data.details})`
    })
    h.loadSource(props.play.url)
    h.attachMedia(media)
    hls = h
  }
  emit('element', media)
  if (props.autoplay) void media.play().catch(() => undefined)
}

/** Loads a fresh source into the same element (whatever holds it carries on), back where it was. */
async function recover(media: HTMLMediaElement): Promise<void> {
  if (!props.reload || retries >= MAX_RETRIES || !alive || el.value !== media) return
  const at = media.currentTime
  const playing = !media.paused || (!!props.autoplay && at === 0)
  await new Promise((r) => setTimeout(r, 1000 * 2 ** retries++))
  let fresh: PlayResponse
  try {
    fresh = await props.reload()
  } catch {
    return
  }
  if (!alive || el.value !== media || fresh.hls) return
  media.addEventListener(
    'loadedmetadata',
    () => {
      media.currentTime = at
      if (playing) void media.play().catch(() => undefined)
    },
    { once: true },
  )
  media.src = fresh.url
}

watch(
  () => props.play.url,
  () => {
    retries = 0
    void attach()
  },
)
watch(el, attach)
onBeforeUnmount(() => {
  alive = false
  hls?.destroy()
  hls = null
  // Removal pauses an element, but anything still holding it (a player's `el`, a sync
  // timer) could play it again, out of sight: it stops itself, and lets its download go
  // (no other player shares it: each stream URL is its own, lib/media.ts).
  const media = el.value
  if (media) {
    media.pause()
    media.addEventListener('play', () => media.pause())
    media.removeAttribute('src')
    try {
      media.load()
    } catch {
      // jsdom
    }
  }
})
</script>

<template>
  <div ref="frame" :class="kind === 'video' ? 'media-frame bg-black text-white' : ''">
    <audio v-if="kind === 'audio'" ref="el" preload="metadata" data-testid="library-player" />
    <video
      v-else
      ref="el"
      preload="metadata"
      playsinline
      class="max-h-[70vh] w-full cursor-pointer bg-black"
      data-testid="library-player"
      @click="controls?.toggle()"
      @dblclick="controls?.toggleFullscreen()"
    />
    <MediaControls
      v-if="!bare"
      ref="controls"
      :el="el"
      :kind="kind"
      :frame="frame"
      :skip="skip"
      :class="kind === 'video' ? 'px-2' : ''"
      @previous="emit('previous')"
      @next="emit('next')"
    />
    <p v-if="failed" class="mono mt-1 text-error">{{ failed }}</p>
  </div>
</template>
